/**
 * Ancho medido de un contenedor, para darle a Recharts un número en vez de un
 * `ResponsiveContainer`.
 *
 * Por qué existe: `ResponsiveContainer` usa `ResizeObserver`, que jsdom no tiene,
 * y su `useEffect` **tira una excepción no capturada** que se lleva puesto el
 * render entero del test. No es un problema del test: significa que el gráfico
 * depende de una API del navegador que no está garantizada. Medir con `clientWidth`
 * y escuchar `resize` cubre el caso real (la ventana cambia de tamaño) y degrada a
 * un ancho por defecto donde no hay layout, en vez de romper.
 *
 * No se agrega a `src/ui`: es una muleta de este módulo para un gráfico concreto,
 * no una primitiva del design system.
 */
import { useEffect, useState, type RefObject } from 'react'

export function useAncho(
  ref: RefObject<HTMLElement | null>,
  fallback = 560,
): number {
  const [ancho, setAncho] = useState(fallback)

  useEffect(() => {
    function medir() {
      const medido = ref.current?.clientWidth ?? 0
      setAncho(medido > 0 ? medido : fallback)
    }

    medir()
    window.addEventListener('resize', medir)
    return () => window.removeEventListener('resize', medir)
  }, [ref, fallback])

  return ancho
}
